/**
 * Bring in your data (step 3.2 of docs/plans/setup-file-data.md, F2): after
 * Build, each sheet of the setup's own files that holds rows, the record or
 * list it goes to, how its columns match, a few of its rows, and the rows
 * that will not fit as they stand and why. The person brings each in, checks
 * it first (the import's own check, where a match can be fixed), or skips
 * it. Bringing it in runs the import job, the same one as Coach's, with Undo
 * for 7 days and rows set aside on the Set aside page.
 *
 *   GET  /v1/setup/data                                (workspace header) → SetupDataResponse
 *   GET  /v1/setup/data/fit?fileId&sheet[&record]      (workspace header) → SetupDataFit
 *   POST /v1/setup/data/files  SetupDataActionRequest  (workspace header) → SetupDataFile
 *   POST /v1/setup/data/offer  SetupDataOfferRequest   (workspace header) → SetupDataResponse
 *
 * Pure: the phone bundles this package, and reads none of it (F7: web only).
 */
import { ASIDE_REASONS, type AsideReason } from './imports';
import { SETUP_BUILD_IMPORT_STATUSES, type SetupBuildImportStatus } from './setup-build';

/** One column of the sheet: its header, and the field it fills (by the field's name), or null when it is not kept. */
export type SetupDataColumn = { readonly header: string; readonly field: string | null };

/** A record or list of the setup a sheet's rows could go to. */
export type SetupDataTarget = { readonly key: string; readonly name: string; readonly list: boolean };

/** One sheet of a setup file that holds rows, as the review step shows it. */
export type SetupDataFile = {
  readonly fileId: string;
  readonly sheet: string;
  /** The file's name, as the person gave it. */
  readonly name: string;
  /** The record or list the rows go to; null when nothing in the setup matched the sheet. */
  readonly target: SetupDataTarget | null;
  readonly rows: number;
  readonly columns: readonly SetupDataColumn[];
  /** The required fields no column fills, by name: every row needs them, and the file says none. */
  readonly unfilled: readonly string[];
  /** The sheet's first rows, by its headers. */
  readonly sample: { readonly headers: readonly string[]; readonly rows: readonly (readonly string[])[] };
  /** pending until the person decides; then started (with its job), failed (with why), or skipped. */
  readonly status: SetupBuildImportStatus;
  readonly jobId?: string;
  readonly reason?: string;
};

/**
 * One reason rows will not fit as they stand: how many, and why in the
 * person's words. Names not on their list yet also say the list they are
 * added to and the required fields each new name is left to fill (R9 of
 * docs/plans/qa-fixes-2.md), when the server knows them.
 */
export type SetupDataUnfit = {
  readonly reason: AsideReason;
  readonly count: number;
  readonly why: string;
  readonly list?: string;
  readonly namesLeaveEmpty?: readonly string[];
};

/** The rows of one sheet judged against its record as it stands, nothing written: how many fit, and why the rest do not. */
export type SetupDataFit = { readonly checked: number; readonly fits: number; readonly unfit: readonly SetupDataUnfit[] };

/**
 * Where the workspace's review stands: `open` once Build (or a yes to the
 * offer) opened it; `offer` for a workspace built before it whose files hold
 * rows nothing brought in, until the person answers; `declined` once they
 * said no; `none` when there is nothing to offer.
 */
export const SETUP_DATA_REVIEWS = ['open', 'offer', 'declined', 'none'] as const;
export type SetupDataReview = (typeof SETUP_DATA_REVIEWS)[number];

export type SetupDataResponse = {
  readonly review: SetupDataReview;
  /** Lists first, so the names rows pick from are in before them. */
  readonly files: readonly SetupDataFile[];
  /** What a sheet the setup matched to nothing could go to. */
  readonly targets: readonly SetupDataTarget[];
};

export const SETUP_DATA_ACTIONS = ['bring', 'check', 'skip'] as const;
/** bring: run the import now. check: run the import's own check first, where a match can be fixed. skip: leave the rows out. */
export type SetupDataAction = (typeof SETUP_DATA_ACTIONS)[number];

export type SetupDataActionRequest = {
  readonly fileId: string;
  readonly sheet: string;
  readonly action: SetupDataAction;
  /** The record or list the person picked, by its key, in place of the one the setup matched. */
  readonly record?: string;
};

export type SetupDataOfferRequest = { readonly answer: 'yes' | 'no' };

/** Files still waiting for the person's word. */
export const setupDataWaiting = (files: readonly Pick<SetupDataFile, 'status'>[]): number => files.filter((one) => one.status === 'pending').length;

/* ---------------------------------------------------------------- readers */

const BOUND = { text: 2000, files: 60, columns: 200, rows: 10, targets: 200 } as const;

const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const isText = (value: unknown, max: number = BOUND.text): value is string => typeof value === 'string' && value.length <= max;
const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isTexts = (value: unknown, max: number): value is string[] => Array.isArray(value) && value.length <= max && value.every((one) => isText(one));

const readTarget = (raw: unknown): SetupDataTarget | null =>
  isObject(raw) && isText(raw.key, 64) && isText(raw.name, 300) && typeof raw.list === 'boolean' ? { key: raw.key, name: raw.name, list: raw.list } : null;

const readFile = (raw: unknown): SetupDataFile | null => {
  if (!isObject(raw)) return null;
  const { fileId, sheet, name, target, rows, columns, unfilled, sample, status, jobId, reason } = raw;
  if (!isText(fileId, 64) || !isText(sheet, 200) || !isText(name, 300) || !isCount(rows)) return null;
  const read = target === null ? null : readTarget(target);
  if (target !== null && read === null) return null;
  if (!Array.isArray(columns) || columns.length > BOUND.columns) return null;
  const cols = columns.map((one) => (isObject(one) && isText(one.header, 300) && (one.field === null || isText(one.field, 300)) ? { header: one.header, field: one.field as string | null } : null));
  if (cols.some((one) => one === null)) return null;
  if (!isTexts(unfilled, BOUND.columns)) return null;
  if (!isObject(sample) || !isTexts(sample.headers, BOUND.columns) || !Array.isArray(sample.rows) || sample.rows.length > BOUND.rows) return null;
  if (!sample.rows.every((row) => isTexts(row, BOUND.columns))) return null;
  if (typeof status !== 'string' || !(SETUP_BUILD_IMPORT_STATUSES as readonly string[]).includes(status)) return null;
  if (jobId !== undefined && !isText(jobId, 64)) return null;
  if (reason !== undefined && !isText(reason)) return null;
  return {
    fileId,
    sheet,
    name,
    target: read,
    rows,
    columns: cols as SetupDataColumn[],
    unfilled: [...unfilled],
    sample: { headers: [...(sample.headers as string[])], rows: (sample.rows as string[][]).map((row) => [...row]) },
    status: status as SetupBuildImportStatus,
    ...(jobId === undefined ? {} : { jobId }),
    ...(reason === undefined ? {} : { reason }),
  };
};

/** One file as `POST /v1/setup/data/files` answers it, or null. */
export const readSetupDataFile = readFile;

/** `GET /v1/setup/data`, or null. */
export const readSetupDataResponse = (raw: unknown): SetupDataResponse | null => {
  if (!isObject(raw)) return null;
  const { review, files, targets } = raw;
  if (typeof review !== 'string' || !(SETUP_DATA_REVIEWS as readonly string[]).includes(review)) return null;
  if (!Array.isArray(files) || files.length > BOUND.files || !Array.isArray(targets) || targets.length > BOUND.targets) return null;
  const read = files.map(readFile);
  const aims = targets.map(readTarget);
  if (read.some((one) => one === null) || aims.some((one) => one === null)) return null;
  return { review: review as SetupDataReview, files: read as SetupDataFile[], targets: aims as SetupDataTarget[] };
};

/** `GET /v1/setup/data/fit`, or null. */
export const readSetupDataFit = (raw: unknown): SetupDataFit | null => {
  if (!isObject(raw) || !isCount(raw.checked) || !isCount(raw.fits) || !Array.isArray(raw.unfit) || raw.unfit.length > 50) return null;
  const unfit = raw.unfit.map((one): SetupDataUnfit | null => {
    if (!isObject(one) || typeof one.reason !== 'string' || !(ASIDE_REASONS as readonly string[]).includes(one.reason) || !isCount(one.count) || !isText(one.why)) return null;
    const read: SetupDataUnfit = { reason: one.reason as AsideReason, count: one.count, why: one.why };
    const { list, namesLeaveEmpty: fill } = one;
    if (list !== undefined && !(isText(list, 300) && list.trim() !== '')) return null;
    if (fill !== undefined && !isTexts(fill, BOUND.columns)) return null;
    return { ...read, ...(list === undefined ? {} : { list }), ...(fill === undefined ? {} : { namesLeaveEmpty: fill }) };
  });
  if (unfit.some((one) => one === null)) return null;
  return { checked: raw.checked, fits: raw.fits, unfit: unfit as SetupDataUnfit[] };
};
